import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ScrollArea } from "./ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  Image as ImageIcon,
  Video,
  FileText,
  Link as LinkIcon,
  Mic,
  Download,
  Loader2,
  ExternalLink,
  Play,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export const SharedMediaPanel = ({ conversationId }) => {
  const [activeTab, setActiveTab] = useState("all");
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (conversationId) {
      fetchMedia(activeTab);
    }
  }, [conversationId, activeTab]);

  const fetchMedia = async (type) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/conversations/${conversationId}/media?media_type=${type}`, {
        headers: { ...getAuthHeaders() },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setMedia(data);
      }
    } catch (err) {
      console.error("Error fetching media:", err);
    }
    setLoading(false);
  };

  const handleDownload = async (item) => {
    if (item.file_url) {
      window.open(item.file_url, '_blank');
    }
  };

  const tabs = [
    { id: "all", label: "Todo", icon: null },
    { id: "images", label: "Fotos", icon: ImageIcon },
    { id: "videos", label: "Videos", icon: Video },
    { id: "files", label: "Archivos", icon: FileText },
    { id: "links", label: "Enlaces", icon: LinkIcon },
    { id: "voice", label: "Audio", icon: Mic },
  ];

  const renderMediaItem = (item) => {
    switch (item.message_type) {
      case "image":
        return (
          <div key={item.message_id} className="relative group">
            <img
              src={item.file_url || item.thumbnail_url}
              alt="Imagen"
              className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80"
              onClick={() => window.open(item.file_url, '_blank')}
            />
            <div className="absolute bottom-1 left-1 text-xs text-white bg-black/50 px-1 rounded">
              {new Date(item.created_at).toLocaleDateString('es-MX')}
            </div>
          </div>
        );

      case "video":
        return (
          <div key={item.message_id} className="relative group">
            <div className="w-full h-24 bg-slate-800 rounded-lg flex items-center justify-center cursor-pointer hover:bg-slate-700"
                 onClick={() => window.open(item.file_url, '_blank')}>
              {item.thumbnail_url ? (
                <img src={item.thumbnail_url} alt="Video" className="w-full h-full object-cover rounded-lg" />
              ) : (
                <Video className="w-8 h-8 text-slate-500" />
              )}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 bg-red-600/80 rounded-full flex items-center justify-center">
                  <Play className="w-5 h-5 text-white ml-0.5" />
                </div>
              </div>
            </div>
            <div className="absolute bottom-1 left-1 text-xs text-white bg-black/50 px-1 rounded">
              {new Date(item.created_at).toLocaleDateString('es-MX')}
            </div>
          </div>
        );

      case "file":
      case "document":
        return (
          <div key={item.message_id} className="flex items-center p-3 bg-[#242f3d] rounded-lg hover:bg-[#2b3a4c] cursor-pointer"
               onClick={() => handleDownload(item)}>
            <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-red-400" />
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-white text-sm truncate">{item.file_name || "Archivo"}</p>
              <p className="text-slate-500 text-xs">{new Date(item.created_at).toLocaleDateString('es-MX')}</p>
            </div>
            <Download className="w-4 h-4 text-slate-400" />
          </div>
        );

      case "voice":
        return (
          <div key={item.message_id} className="flex items-center p-3 bg-[#242f3d] rounded-lg hover:bg-[#2b3a4c] cursor-pointer"
               onClick={() => handleDownload(item)}>
            <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
              <Mic className="w-5 h-5 text-green-400" />
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-white text-sm">Mensaje de voz</p>
              <p className="text-slate-500 text-xs">{new Date(item.created_at).toLocaleDateString('es-MX')}</p>
            </div>
            <Play className="w-4 h-4 text-slate-400" />
          </div>
        );

      default:
        // Links/URL previews
        if (item.url_preview) {
          return (
            <div key={item.message_id} className="p-3 bg-[#242f3d] rounded-lg hover:bg-[#2b3a4c] cursor-pointer"
                 onClick={() => window.open(item.url_preview.url, '_blank')}>
              <div className="flex items-start">
                {item.url_preview.image && (
                  <img src={item.url_preview.image} alt="" className="w-16 h-16 object-cover rounded mr-3" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{item.url_preview.title || "Enlace"}</p>
                  <p className="text-slate-400 text-xs truncate">{item.url_preview.description}</p>
                  <div className="flex items-center mt-1 text-slate-500 text-xs">
                    <LinkIcon className="w-3 h-3 mr-1" />
                    <span className="truncate">{item.url_preview.url}</span>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400 ml-2 flex-shrink-0" />
              </div>
              <p className="text-slate-500 text-xs mt-2">{new Date(item.created_at).toLocaleDateString('es-MX')}</p>
            </div>
          );
        }
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#17212b]">
      <div className="p-3 border-b border-slate-700/50">
        <h3 className="text-white font-medium text-center">Media Compartida</h3>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-700/50 overflow-x-auto">
        <div className="flex min-w-max">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "text-red-400 border-b-2 border-red-400"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              {tab.icon && <tab.icon className="w-4 h-4 mr-1" />}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 p-3">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-red-500" />
          </div>
        ) : media.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No hay contenido compartido</p>
          </div>
        ) : (
          <div className={`${
            activeTab === "images" || activeTab === "videos" 
              ? "grid grid-cols-3 gap-2" 
              : "space-y-2"
          }`}>
            {media.map(item => renderMediaItem(item))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default SharedMediaPanel;
